import React, { useEffect, useState } from "react";
import axios from "axios";

const FamilyDashboard = () => {
  const [family, setFamily] = useState(null);
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [profileForm, setProfileForm] = useState({
    leaderName: "",
    email: "",
    phone: "",
    address: "",
    members: ""
  });
  const [isEditing, setIsEditing] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const token = localStorage.getItem("familyToken");

  // Fetch family profile
  useEffect(() => {
    const fetchFamily = async () => {
      if (!token) {
        setMsg("⚠️ No authentication token found. Please login.");
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE}/api/family/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFamily(res.data);
        // Initialize profile form with current data
        setProfileForm({
          leaderName: res.data.leaderName || "",
          email: res.data.email || "",
          phone: res.data.phone || "",
          address: res.data.address || "",
          members: Array.isArray(res.data.members) ? res.data.members.join(", ") : ""
        });
        setMsg("");
      } catch (err) {
        console.error("Error fetching family profile:", err.response || err);
        setMsg(err.response?.data?.message || "⚠️ Failed to load family data. Please login again.");
      } finally {
        setLoading(false);
      }
    };
    fetchFamily();
  }, [token, API_BASE]);

  // Handle input changes properly
  const handleInputChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Enable editing mode
  const enableEditing = () => {
    setIsEditing(true);
  };

  // Cancel editing
  const cancelEditing = () => {
    // Reset form to original family data
    if (family) {
      setProfileForm({
        leaderName: family.leaderName || "",
        email: family.email || "",
        phone: family.phone || "",
        address: family.address || "",
        members: Array.isArray(family.members) ? family.members.join(", ") : ""
      });
    }
    setIsEditing(false);
    setMsg("");
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!family) return;

    try {
      setLoading(true);
      setMsg("");

      const updateData = {
        leaderName: profileForm.leaderName.trim(),
        email: profileForm.email.trim(),
        phone: profileForm.phone.trim(),
        address: profileForm.address.trim(),
        members: profileForm.members ? 
          profileForm.members.split(",")
            .map(m => m.trim())
            .filter(m => m) 
          : []
      };

      // Validation
      if (!updateData.leaderName) {
        setMsg("⚠️ Leader name is required");
        return;
      }

      if (!updateData.email) {
        setMsg("⚠️ Email is required");
        return;
      }

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(updateData.email)) {
        setMsg("⚠️ Please enter a valid email address");
        return;
      }

      const res = await axios.put(`${API_BASE}/api/family/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFamily(res.data.family);
      setIsEditing(false);
      setMsg("✅ Profile updated successfully!");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error("Profile update error:", err.response || err);
      setMsg(err.response?.data?.message || "⚠️ Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // Handle document upload with improved error handling
  const handleUpload = async () => {
    if (!file) {
      setMsg("⚠️ Please select a file");
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setMsg("⚠️ File size too large. Maximum size is 10MB.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file); // Changed from "document" to "file" to match backend

    try {
      const res = await axios.post(`${API_BASE}/api/family/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 30000, // 30 second timeout
      });
      
      if (res.data.family) {
        setFamily(res.data.family);
      }
      setFile(null);
      setMsg("✅ File uploaded successfully");
      
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = "";
      
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error("Upload error:", err.response || err);
      if (err.response?.status === 500) {
        setMsg("⚠️ Server error during upload. Please try again.");
      } else if (err.code === 'ECONNABORTED') {
        setMsg("⚠️ Upload timeout. Please try again.");
      } else {
        setMsg(err.response?.data?.message || "⚠️ Upload failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle document download with improved error handling
  const handleDownload = async (document) => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_BASE}/api/family/document/${document.storedName}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 30000,
      });

      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = document.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMsg("✅ Download started");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error("Download error:", err);
      setMsg("⚠️ Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle document delete with improved error handling
  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    setLoading(true);
    try {
      const res = await axios.delete(`${API_BASE}/api/family/document/${docId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      
      if (res.data.family) {
        setFamily(res.data.family);
      }
      setMsg("✅ Document deleted successfully");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error("Delete error:", err.response || err);
      if (err.response?.status === 500) {
        setMsg("⚠️ Server error during deletion. Please try again.");
      } else if (err.response?.status === 404) {
        setMsg("⚠️ Document not found. It may have been already deleted.");
      } else {
        setMsg(err.response?.data?.message || "⚠️ Failed to delete document");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle tax delete with improved error handling
  const handleDeleteTax = async (taxId) => {
    if (!window.confirm("Are you sure you want to delete this tax record?")) return;
    
    setLoading(true);
    try {
      const res = await axios.delete(`${API_BASE}/api/family/tax/${taxId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      
      if (res.data.family) {
        setFamily(res.data.family);
      }
      setMsg("✅ Tax record deleted successfully");
      setTimeout(() => setMsg(""), 3000);
    } catch (err) {
      console.error("Delete tax error:", err.response || err);
      if (err.response?.status === 500) {
        setMsg("⚠️ Server error during deletion. Please try again.");
      } else if (err.response?.status === 404) {
        setMsg("⚠️ Tax record not found. It may have been already deleted.");
      } else {
        setMsg(err.response?.data?.message || "⚠️ Failed to delete tax record");
      }
    } finally {
      setLoading(false);
    }
  };

  // Navigation Tabs Component
  const NavigationTabs = () => (
    <div className="flex overflow-x-auto border-b mb-6 bg-white rounded-lg shadow-sm">
      {[
        { id: "profile", label: "Profile", icon: "👤" },
        { id: "tax", label: "Tax Records", icon: "💰" },
        { id: "documents", label: "Documents", icon: "📁" }
      ].map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`flex-1 min-w-32 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === tab.id
              ? "border-b-2 border-green-600 text-green-600 bg-green-50"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          <span className="mr-2">{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  );

  // Profile Section
  const ProfileSection = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-gray-800">Family Information</h3>
        {!isEditing && (
          <button
            onClick={enableEditing}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
          >
            <span>✏️</span>
            Edit Profile
          </button>
        )}
      </div>
      
      {/* Profile Display - Show when not editing */}
      {!isEditing && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-gray-50 p-4 border-b md:border-b-0 md:border-r">
              <div className="space-y-4">
                <InfoRow label="Family ID" value={family.familyId} />
                <InfoRow label="Leader Name" value={family.leaderName} />
                <InfoRow label="Email" value={family.email} />
                <InfoRow label="Status" value={
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    family.approved 
                      ? "bg-green-100 text-green-800" 
                      : "bg-yellow-100 text-yellow-800"
                  }`}>
                    {family.approved ? "Approved" : "Pending Approval"}
                  </span>
                } />
              </div>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <InfoRow label="Phone" value={family.phone} />
                <InfoRow label="Address" value={family.address} />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Family Members</label>
                  <div className="text-gray-900">
                    {family.members && family.members.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {family.members.map((member, index) => (
                          <span
                            key={index}
                            className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
                          >
                            {member}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500">No members listed</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Update Form - Show when editing */}
      {isEditing && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-lg text-gray-800">Update Profile</h4>
            <button
              onClick={cancelEditing}
              className="text-gray-600 hover:text-gray-800 font-medium text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leader Name *
                </label>
                <input
                  type="text"
                  value={profileForm.leaderName}
                  onChange={(e) => handleInputChange("leaderName", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  required
                  placeholder="Enter leader name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  required
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profileForm.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={profileForm.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Enter full address"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Family Members (comma separated)
              </label>
              <textarea
                value={profileForm.members}
                onChange={(e) => handleInputChange("members", e.target.value)}
                rows="3"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                placeholder="Enter family members separated by commas (e.g., John Doe, Jane Smith, etc.)"
              />
              <p className="mt-1 text-sm text-gray-500">
                Separate each family member with a comma
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium flex-1 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={loading}
                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium flex-1"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );

  // Tax Section
  const TaxSection = () => (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h3 className="text-xl font-bold text-gray-800">Tax Records</h3>
        <div className="text-sm text-gray-600">
          Total Records: <span className="font-semibold">{family.taxHistory ? family.taxHistory.length : 0}</span>
        </div>
      </div>

      {family.taxHistory && family.taxHistory.length > 0 ? (
        <>
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left font-medium text-gray-700">Month</th>
                    <th className="p-3 text-left font-medium text-gray-700">Amount (₹)</th>
                    <th className="p-3 text-left font-medium text-gray-700">Status</th>
                    <th className="p-3 text-left font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {family.taxHistory.map((tax) => (
                    <tr key={tax._id} className="hover:bg-gray-50">
                      <td className="p-3 text-gray-900 font-medium">{tax.month}</td>
                      <td className="p-3 text-gray-900">₹{tax.amount || 0}</td>
                      <td className="p-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            tax.paid
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {tax.paid ? "Paid" : "Pending"}
                        </span>
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => handleDeleteTax(tax._id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 disabled:text-gray-400 font-medium text-sm transition-colors px-3 py-1 border border-red-600 rounded hover:bg-red-50"
                          title="Delete tax record"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tax Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {family.taxHistory.filter(tax => tax.paid).length}
              </div>
              <div className="text-sm text-green-700 font-medium">Paid Taxes</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-600">
                {family.taxHistory.filter(tax => !tax.paid).length}
              </div>
              <div className="text-sm text-red-700 font-medium">Pending Taxes</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                ₹{family.taxHistory.reduce((sum, tax) => sum + (tax.paid ? Number(tax.amount || 0) : 0), 0)}
              </div>
              <div className="text-sm text-blue-700 font-medium">Total Paid</div>
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
          <div className="text-gray-400 text-6xl mb-4">💰</div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Tax Records</h4>
          <p className="text-gray-600">No tax records found for your family.</p>
        </div>
      )}
    </div>
  );

  // Documents Section
  const DocumentsSection = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800">Document Management</h3>

      {/* Upload Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h4 className="font-semibold text-lg mb-4 text-gray-800">Upload New Document</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Document
            </label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
              className="block w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 transition-colors"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
            />
            <p className="mt-1 text-sm text-gray-500">
              Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (Max 10MB)
            </p>
          </div>
          
          {file && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-900">{file.name}</p>
                  <p className="text-sm text-blue-700">
                    Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p className="text-xs text-blue-600">
                    Type: {file.type || "Unknown"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-red-600 hover:text-red-800 font-medium text-sm px-3 py-1 border border-red-600 rounded hover:bg-red-50 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
          
          <button
            type="button"
            onClick={handleUpload}
            disabled={!file || loading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium w-full flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              "Upload Document"
            )}
          </button>
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-4 border-b">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <span>Uploaded Documents</span>
            <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm">
              {family.documents ? family.documents.length : 0}
            </span>
          </h4>
        </div>

        {family.documents && family.documents.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {family.documents.map((doc) => (
              <div key={doc._id} className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      {getFileIcon(doc.originalName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 truncate">
                        {doc.originalName || "Unnamed Document"}
                      </div>
                      <div className="text-sm text-gray-600">
                        Uploaded: {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : "Unknown date"}
                      </div>
                      {doc.fileSize && (
                        <div className="text-xs text-gray-500 mt-1">
                          Size: {(doc.fileSize / 1024 / 1024).toFixed(2)} MB • Type: {doc.mimeType || "Unknown"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() => handleDownload(doc)}
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-medium transition-colors text-center flex-1 sm:flex-none flex items-center justify-center gap-2"
                  >
                    <span>📥</span>
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(doc._id)}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex-1 sm:flex-none flex items-center justify-center gap-2"
                  >
                    <span>🗑️</span>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">📁</div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Documents</h4>
            <p className="text-gray-600">No documents uploaded yet. Upload your first document above.</p>
          </div>
        )}
      </div>
    </div>
  );

  // Helper function for file icons
  const getFileIcon = (filename) => {
    if (!filename) return '📎';
    
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      case 'txt':
        return '📃';
      default:
        return '📎';
    }
  };

  // Helper component for info rows
  const InfoRow = ({ label, value }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="text-gray-900">
        {value !== undefined && value !== null && value !== '' ? value : <span className="text-gray-500">-</span>}
      </div>
    </div>
  );

  if (!family) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg font-semibold text-gray-700">Loading family data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8 text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-green-700 mb-2">
            🏠 Family Dashboard
          </h1>
          <p className="text-gray-600">Welcome back, {family.leaderName}!</p>
        </div>

        {/* Status Messages */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center mb-6">
            <div className="flex items-center justify-center gap-2 text-blue-700">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
              Processing...
            </div>
          </div>
        )}

        {msg && (
          <div className={`p-4 rounded-lg text-center mb-6 ${
            msg.includes("✅") 
              ? "bg-green-50 border border-green-200 text-green-700"
              : "bg-yellow-50 border border-yellow-200 text-yellow-700"
          }`}>
            {msg}
          </div>
        )}

        {/* Navigation Tabs */}
        <NavigationTabs />

        {/* Content Section */}
        <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
          {activeTab === "profile" && <ProfileSection />}
          {activeTab === "tax" && <TaxSection />}
          {activeTab === "documents" && <DocumentsSection />}
        </div>
      </div>
    </div>
  );
};

export default FamilyDashboard;